import { forwardRef } from 'react';
import type { Shape } from '../types/slide-doc';
import { useStageDims } from './Stage';

interface Props {
  shape: Shape;
}

// Renders a single shape inside the logical stage.
// shape.anchor='center' means x,y is offset from stage center → works in both orientations.
export const ShapeRenderer = forwardRef<HTMLDivElement | SVGSVGElement, Props>(
  function ShapeRenderer({ shape }, ref) {
    const stage = useStageDims();
    let left = shape.x;
    let top = shape.y;
    if (shape.anchor === 'center') {
      left = stage.w / 2 + shape.x - shape.w / 2;
      top = stage.h / 2 + shape.y - shape.h / 2;
    }
    const base: React.CSSProperties = {
      position: 'absolute',
      left,
      top,
      width: shape.w,
      height: shape.h,
      opacity: shape.opacity ?? 1,
      transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
      zIndex: shape.zIndex,
      pointerEvents: 'none',
    };

    if (shape.type === 'image') {
      return (
        <div ref={ref as React.Ref<HTMLDivElement>} data-shape-id={shape.id} style={base}>
          <img
            src={shape.src}
            alt={shape.alt ?? ''}
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' }}
          />
        </div>
      );
    }

    if (shape.type === 'rect') {
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          data-shape-id={shape.id}
          style={{
            ...base,
            background: shape.fill ?? 'transparent',
            border: shape.stroke ? `${shape.strokeWidth ?? 2}px solid ${shape.stroke}` : undefined,
            borderRadius: shape.radius,
          }}
        />
      );
    }

    if (shape.type === 'text') {
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          data-shape-id={shape.id}
          style={{
            ...base,
            color: shape.color ?? '#1A1A1A',
            fontSize: shape.fontSize ?? 48,
            fontWeight: shape.fontWeight ?? 600,
            textAlign: shape.align ?? 'left',
            fontFamily: shape.fontFamily ?? 'Inter, system-ui, sans-serif',
            display: 'flex',
            alignItems: 'center',
            justifyContent:
              shape.align === 'center' ? 'center' : shape.align === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          {shape.text}
        </div>
      );
    }

    // circle — SVG so we can animate stroke-dashoffset for drawStroke
    if (shape.type === 'circle') {
      const cx = shape.w / 2;
      const cy = shape.h / 2;
      const r = Math.min(shape.w, shape.h) / 2 - (shape.strokeWidth ?? 4);
      const circumference = 2 * Math.PI * r;
      return (
        <svg
          ref={ref as React.Ref<SVGSVGElement>}
          data-shape-id={shape.id}
          width={shape.w}
          height={shape.h}
          viewBox={`0 0 ${shape.w} ${shape.h}`}
          style={base}
        >
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill={shape.fill ?? 'none'}
            stroke={shape.stroke ?? '#F5B800'}
            strokeWidth={shape.strokeWidth ?? 4}
            strokeDasharray={circumference}
            strokeDashoffset={0}
            data-circumference={circumference}
            style={{ transformOrigin: '50% 50%' }}
          />
        </svg>
      );
    }

    return null;
  }
);
